<template>
  <v-dialog v-model="openDialogue" :max-width="dialogWidth" persistent>
    <v-card>
      <v-card-title>
        {{ title }}
      </v-card-title>
      <v-divider class="mx-6"></v-divider>
      <v-card-text class="py-4" v-if="!newDownloadRequest && fileGeneratedHistoryData.length===0">
        <v-form ref="fileGenerationForm">
          <v-row class="justify-start filter-row">
            <v-col cols="12" xs="12" md="4" class="field-spacing">
              <div class="d-flex align-center">
                <v-menu
                  ref="fileDateRangeModal"
                  v-model="fileDateRangeModal"
                  :close-on-content-click="false"
                  :return-value.sync="fileDateRange"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="dateRangeText"
                      :placeholder="
                        $vuetify.lang.t('$vuetify.common.daterange_placeholder_txt')
                      "
                      readonly
                      outlined
                      dense
                      hide-details="auto"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[
                        (v) =>
                          (v.split(' ~ ').length === 2 &&
                            new Date(v.split(' ~ ')[0]) <= new Date(v.split(' ~ ')[1]))
                          || $vuetify.lang.t('$vuetify.logs_file_generation.date_range_validation')
                      ]"
                    ></v-text-field>
                  </template>
                <v-date-picker
                  v-model="fileDateRange"
                  color="primary"
                  no-title
                  scrollable
                  range
                  :max="dateOfToday()"
                  :min="fileDateRange && fileDateRange.length === 1 ? fileDateRange[0] : null"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="error" @click="fileDateRangeModal = false">
                    {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
                  </v-btn>
                  <v-btn text color="primary" @click="setDates">
                    {{ $vuetify.lang.t("$vuetify.common.ok_btn") }}
                  </v-btn>
                </v-date-picker>
                </v-menu>
              </div>
            </v-col>
            <!-- Language Selector -->
             <!-- :rules="[(v) => !!v || $vuetify.lang.t('$vuetify...')]" -->
            <v-col cols="12" xs="12" md="4" class="field-spacing">
              <div class="d-flex align-center">
                <v-select
                  v-model="selectedLanguage"
                  :items="languageOptions"
                  item-text="label"
                  item-value="value"
                  :label="$vuetify.lang.t('$vuetify.logs_file_generation.language')"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.CONVERSATION || history_type === HISTORY_TYPES.SESSION" md="4">
              <v-autocomplete
                v-model="selectedProfileId"
                :items="gptProfiles"
                item-text="name"
                item-value="id"
                :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_profile_name')"
                outlined
                dense
                clearable
                hide-details
                :loading="loadingProfiles"
              >
                <template
                  #append-item
                  v-if="hasMoreProfiles"
                >
                  <v-list-item>
                    <v-list-item-content>
                      <div class="d-flex">
                        <v-btn
                          class="ma-0 pa-0"
                          @click="getGPTProfiles()"
                          color="primary"
                          text
                        >
                          {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                        </v-btn>
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col v-if="history_type === HISTORY_TYPES.CONVERSATION || history_type === HISTORY_TYPES.SESSION || history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" cols="12" xs="12" md="4" class="field-spacing">
              <v-text-field
                v-model="searchText"
                :label="$vuetify.lang.t('$vuetify.questionnaire_responses.search_text')"
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.CONVERSATION" md="4">
              <v-select
                v-model="endpointFilter"
                :items="endpointOptions"
                :label="
                  $vuetify.lang.t('$vuetify.gpt_history.gpt_history_filter_endpoints')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.CONVERSATION" md="4">
              <v-select
                v-model="feedbackFilter"
                :items="feedbackOptions"
                :label="
                  $vuetify.lang.t('$vuetify.gpt_history.gpt_history_filter_feedback')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.CONVERSATION" md="4">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                :label="
                  $vuetify.lang.t('$vuetify.gpt_history.status')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.CONVERSATION || history_type === HISTORY_TYPES.SESSION" md="4">
              <v-select
                v-model="archiveFilter"
                :items="archiveOptions"
                :label="
                  $vuetify.lang.t('$vuetify.gpt_history.filter_archive')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.SESSION" md="4">
              <v-text-field
                v-model="filterByCode"
                :label="
                  $vuetify.lang.t('$vuetify.gpt_sessions.search_session_by_code')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.SESSION || history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" md="4">
              <v-text-field
                v-model="filterBySessionId"
                :label="
                  history_type === HISTORY_TYPES.SESSION
                    ? $vuetify.lang.t('$vuetify.gpt_sessions.search_session_by_ID')
                    : $vuetify.lang.t('$vuetify.questionnaire_responses.search_by_session_id')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" md="4">
              <v-text-field
                v-model="filterByUserId"
                :label="
                  $vuetify.lang.t('$vuetify.questionnaire_responses.search_by_user_id')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col v-if="history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" cols="12" xs="12" md="4" class="field-spacing">
              <v-text-field
                v-model="filterByMemberId"
                :label="
                  $vuetify.lang.t('$vuetify.questionnaire_responses.search_by_member_id')
                "
                outlined
                dense
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" xs="12" class="field-spacing" v-if="history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" md="4">
              <v-autocomplete
                v-model="selectedQuestionnaireIds"
                :items="questionnaires"
                item-text="name"
                item-value="id"
                :label="
                  $vuetify.lang.t(
                    '$vuetify.questionnaire_responses.filter_by_questionnaire'
                  )
                "
                outlined
                dense
                clearable
                hide-details
                multiple
                small-chips
                deletable-chips
                :loading="loadingQuestionnaires"
              >
                <template #selection="{ attrs, item, selected }">
                  <thin-scroll-chip
                    :item="item"
                    :selected="selected"
                    v-bind="attrs"
                    @close="removeQuestionnaire(item)"
                  />
                </template>
                <template v-if="hasMoreQuestionnaires" #append-item>
                  <v-list-item>
                    <v-list-item-content>
                      <div class="d-flex">
                        <v-btn
                          class="ma-0 pa-0"
                          color="primary"
                          text
                          @click="getQuestionnaires()"
                        >
                          {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                        </v-btn>
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col v-if="history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE" cols="12" xs="12" md="4" class="field-spacing">
              <v-autocomplete
                v-model="selectedProfileIds"
                :items="gptProfiles"
                item-text="name"
                item-value="id"
                :label="$vuetify.lang.t('$vuetify.questionnaire_responses.filter_by_profile')"
                outlined
                dense
                clearable
                hide-details
                multiple
                small-chips
                deletable-chips
                :loading="loadingProfiles"
              >
                <template #selection="{ attrs, item, selected }">
                  <thin-scroll-chip
                    :item="item"
                    :selected="selected"
                    v-bind="attrs"
                    @close="removeProfile(item)"
                  />
                </template>
                <template
                  v-if="hasMoreProfiles"
                  #append-item
                >
                  <v-list-item>
                    <v-list-item-content>
                      <div class="d-flex">
                        <v-btn
                          class="ma-0 pa-0"
                          color="primary"
                          text
                          @click="getGPTProfiles()"
                        >
                          {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                        </v-btn>
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!--
            [CSV column selection] Column checkbox section (conversation + session only).
            - supportsColumnSelection: true for CONVERSATION and SESSION, false for questionnaire.
            - availableColumns: fetched from the backend on open (see getAvailableColumns), in the
              global UI language. Each item is {key, label, selected_by_default}.
            - selectedColumns: array of keys bound to every checkbox via v-model; starts as the
              keys flagged selected_by_default (conversation: all but the opt-in intro/system;
              session: all). "Select all" ticks every key including the opt-in ones.
            - Lives inside the same <v-form> as the filters, so it stays in component state across
              the two-press "files already exist" flow without any extra handling.
          -->
          <template v-if="supportsColumnSelection">
            <v-divider class="my-4"></v-divider>
            <!-- Row 1: section title (conversation vs session wording via columnSelectionTitleKey) -->
            <v-row no-gutters class="mb-1">
              <v-col cols="12" class="font-weight-bold">
                {{ $vuetify.lang.t(`$vuetify.logs_file_generation.${columnSelectionTitleKey}`) }}
              </v-col>
            </v-row>
            <!-- Row 2: "n of N selected" counter on the left, Select all / Clear all on the right.
                 Hidden until the list has loaded so the counter never shows "0 of 0". -->
            <v-row align="center" no-gutters class="mb-3" v-if="availableColumns.length > 0">
              <v-col cols="12" sm="auto">
                <span class="text-caption grey--text text--darken-1">
                  {{
                    $vuetify.lang.t(
                      "$vuetify.logs_file_generation.columns_selected_txt",
                      selectedColumns.length,
                      availableColumns.length
                    )
                  }}
                </span>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap">
                <v-btn
                  class="pl-0"
                  text
                  x-small
                  color="primary"
                  :disabled="allColumnsSelected"
                  @click="selectAllColumns"
                >
                  {{ $vuetify.lang.t("$vuetify.logs_file_generation.select_all_columns") }}
                </v-btn>
                <v-btn
                  text
                  x-small
                  color="primary"
                  :disabled="selectedColumns.length === 0"
                  @click="clearAllColumns"
                >
                  {{ $vuetify.lang.t("$vuetify.logs_file_generation.clear_all_columns") }}
                </v-btn>
              </v-col>
            </v-row>

            <!-- State A: list still loading -> skeleton placeholder -->
            <v-skeleton-loader v-if="loadingColumns" type="list-item@3"></v-skeleton-loader>

            <!-- State B: fetch failed or returned nothing -> warning only. Generate stays enabled
                 and columnsPayload returns null, so the file falls back to ALL columns (no dead end). -->
            <v-alert
              v-else-if="columnsLoadFailed"
              type="warning"
              dense
              outlined
              text
              class="mb-0"
            >
              {{ $vuetify.lang.t("$vuetify.logs_file_generation.columns_load_failed_txt") }}
            </v-alert>

            <!-- State C: the grid. One checkbox per column; v-model on an array + :value=key means
                 ticking/unticking adds/removes that key from selectedColumns.  -->
            <template v-else>
              <v-row>
                <v-col
                  v-for="column in availableColumns"
                  :key="column.key"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="pb-3"
                >
                  <v-checkbox
                    v-model="selectedColumns"
                    :value="column.key"
                    :label="column.label"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <!-- Inline error when nothing is ticked; Generate is disabled in that state too. -->
              <div
                v-if="selectedColumns.length === 0"
                class="error--text text-caption mt-2"
              >
                {{ $vuetify.lang.t("$vuetify.logs_file_generation.no_column_selected_txt") }}
              </div>
            </template>
          </template>

          <!--
            [CSV column selection - session] "Include questionnaire answers" section.
            It switches the dynamic question/answer columns on/off as a whole.
          -->
          <template v-if="history_type === HISTORY_TYPES.SESSION">
            <v-divider class="my-4"></v-divider>
            <v-row no-gutters>
              <v-col cols="12">
                <v-checkbox
                  v-model="includeQuestionnaireAnswers"
                  :label="$vuetify.lang.t('$vuetify.logs_file_generation.include_questionnaire_answers')"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                ></v-checkbox>
                <div class="text-caption grey--text text--darken-1">
                  {{ $vuetify.lang.t("$vuetify.logs_file_generation.include_questionnaire_answers_hint") }}
                </div>
              </v-col>
            </v-row>
          </template>
        </v-form>
      </v-card-text>
      <v-card-text class="py-4" v-else>
        <v-card-text v-if="fileGeneratedHistoryData.length > 0 && !newDownloadRequest" >
          <v-alert type="info" dense outlined class="mb-4">
              <strong>Notice:</strong> {{ $vuetify.lang.t("$vuetify.logs_file_generation.files_already_exist_txt") }}
              <span class="text--primary font-weight-bold">{{ dateRangeText }}</span>.
              <br>
              {{ fileGenerationInstructionTxt }}
          </v-alert>
          <v-data-table
              :headers="fileGeneratedHistoryHeaders"
              :items="fileGeneratedHistoryData"
              :loading="fileGeneratedHistoryLoading"
              :server-items-length="totalHits"
              :items-per-page="itemsPerPage"
              :no-data-text="$vuetify.lang.t('$vuetify.common.no_data_available')"
              :options.sync="options"
              @update:options="getTasks"
              :footer-props="footerProps"
              disable-sort
              mobile-breakpoint="0"
              class="file-history-table"
              >
              <template v-slot:body="{ items }">
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <template>
                      <td>{{ getRequestTime(item.name) }}</td>
                      <td>
                        {{ getDateRange(item.name) }}
                      </td>
                      <td>
                        <v-chip :color="item.status === 'success' ? 'green' : item.status === 'failed' ? 'red' : 'grey'"
                          dark>
                          {{ item.status }}
                        </v-chip>
                      </td>
                      <td>
                        {{ mapLocaleToLanguage(item.locale) }}
                      </td>
                      <td>
                        <span v-if="item.status == 'success' && getFileExpirationFromTaskName(item.name)"
                          @click="generatePresignedUrl(getDownloadUrl(item.message))"
                          style="cursor: pointer; color: blue; text-decoration: underline;">
                          Download
                        </span>
                        <span v-else>N/A</span>
                      </td>
                      <td>
                        {{ item.status == "success" && getFileExpirationFromTaskName(item.name) ? getFileExpirationFromTaskName(item.name) : item.status === "pending" ? "N/A" :  "Expired"  }}
                      </td>
                    </template>

                  </tr>
                </tbody>
              </template>
          </v-data-table>
        </v-card-text>
          
        </v-card-text>
        
        <v-card-text v-if="newDownloadRequest" >
          {{downloadRequestTxt}}
        </v-card-text>
      <v-divider class="mx-6"></v-divider>
      <v-card-actions class="justify-end px-4 pt-4 pb-4" v-if="fileGeneratedHistoryData.length === 0 && !newDownloadRequest">
        <v-btn outlined color="error" @click="closeModal">{{
          $vuetify.lang.t("$vuetify.common.cancel_btn")
        }}</v-btn>
        <v-btn
          color="primary"
          :loading="fileGenerationLoading"
          :disabled="isGenerateDisabled"
          @click="getConversationHistory"
          >{{ $vuetify.lang.t("$vuetify.common.generate_btn") }}</v-btn
        >
      </v-card-actions>
      <v-card-actions class="justify-end px-4 pt-4 pb-4" v-else-if="newDownloadRequest">
        <v-btn outlined color="error" @click="closeResponse">{{
          $vuetify.lang.t("$vuetify.common.close_btn")
        }}</v-btn>
      </v-card-actions>
      <v-card-actions class="justify-end px-4 pt-4 pb-4" v-else>
        <v-btn outlined color="error" @click="closeResponse">{{
          $vuetify.lang.t("$vuetify.common.close_btn")
        }}</v-btn>
        <v-btn
          color="primary"
          :loading="fileGenerationLoading"
          :disabled="isGenerateDisabled"
          @click="downloadRequest"
          >
          {{$vuetify.lang.t("$vuetify.common.generate_btn")}}
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { ENDPOINT_TYPES, HISTORY_TYPES } from "@/global/_constants";
import { dateOfToday } from "@/helpers/helper";
import { ADD_ALERT, GENERATE_PRESIGNED_URL, GET_CONVERSATION_HISTORY_COLUMNS, GET_GPT_PROFILES, GET_SESSION_HISTORY_COLUMNS, GET_TASKS_BY_PREFIX, MAIRA_QUESTIONNAIRE } from "@/store/_actiontypes";
import { getDateRange, getDownloadUrl, getFileExpirationFromTaskName, getRequestTime, mapLocaleToLanguage } from "../../helpers/fileGeneration";
import ThinScrollChip from "./ThinScrollChip";

export default {
  components: {
    ThinScrollChip,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    requestFileGeneration: {
      type: Function,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    downloadRequestTxt: {
      type: String,
      required: true,
    },
    fileGenerationInstructionTxt: {
      type: String,
      required: true,
    },
    history_type: {
      type: String,
      required: true,
    },
    prefix: {
      type: String,
      required: true
    },
    filters: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      openDialogue: false,
      dateOfToday,
      fileDateRange: this.filters?.start_date && this.filters?.end_date ? [this.filters.start_date, this.filters.end_date] : [],
      fileDateRangeModal: false,
      fileGenerationLoading: false,
      response: false,
      fileGeneratedHistory: false,
      fileGeneratedHistoryData: [],
      totalHits: 0,
      loadingAvailableHistory: false,
      fileGeneratedHistoryLoading: false,
      options: { page: 1, itemsPerPage: 5 },
      newDownloadRequest: false,
      footerProps: {'items-per-page-options': [5, 15, 25]},
      itemsPerPage: 5,
      selectedLanguage: 'en',
      HISTORY_TYPES: HISTORY_TYPES,
      languageOptions: [
        { label: 'English', value: 'en' },
        { label: 'Japanese', value: 'ja' }
      ],
      fileGeneratedHistoryHeaders: [
        {
          text: this.$vuetify.lang.t(
            "$vuetify.logs_file_generation.request_date"
          ),
          value: "date",
          align: 'start',
          sortable:  "false"
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.logs_file_generation.date_range"
          ),
          value: "date_range",
          sortable: "false"
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.status"),
          value: "status",
          sortable: "false"
        },
        {
          text: this.$vuetify.lang.t("$vuetify.logs_file_generation.language"),
          value: "locale",
          sortable: "false"
        },
        {
          text: this.$vuetify.lang.t("$vuetify.logs_file_generation.file_url"),
          value: "file_url",
          sortable: "false"
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.logs_file_generation.expiration"
          ),
          value: "expiration",
          sortable: "false"
        },
      ],
      gptProfiles: [],
      currentPage: 0,
      pageSize: 100,
      hasMoreProfiles: false,
      loadingProfiles: false,
      selectedProfileId: this.filters?.selectedProfileId || null,
      selectedProfileIds: this.filters?.selectedProfileIds || [],
      filterByCode: this.filters?.searchByCode || null,
      filterBySessionId: this.filters?.searchBySessionId || null,
      searchText: this.filters?.search_text || null,
      filterByUserId: this.filters?.searchByUserId || null,
      filterByMemberId: this.filters?.searchByMemberId || null,
      selectedQuestionnaireIds: this.filters?.selectedQuestionnaireIds || [],
      questionnaires: [],
      questionnairePage: 0,
      questionnairePageSize: 100,
      hasMoreQuestionnaires: false,
      loadingQuestionnaires: false,
      feedbackFilter: this.filters?.feedbackFilter || null,
      endpointFilter: this.filters?.endpointFilter || null,
      statusFilter: this.filters?.statusFilter ?? null,
      archiveFilter: this.filters?.archiveFilter ?? null,
      feedbackOptions: ["positive", "negative"],
      statusOptions: [
        { text: "Seen", value: true },
        { text: "Unseen", value: false }
      ],
      archiveOptions: [
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.active"), value: false },
        { text: this.$vuetify.lang.t("$vuetify.gpt_history.archived"), value: true }
      ],
      endpointOptions: [ENDPOINT_TYPES.ASK, ENDPOINT_TYPES.VISION, ENDPOINT_TYPES.REALTIME, ENDPOINT_TYPES.QUESTIONNAIRE],
      // [CSV column selection] state
      availableColumns: [],        // [{key, label, selected_by_default}] from the backend, canonical order
      selectedColumns: [],         // keys currently ticked; starts as the selected_by_default keys once loaded
      loadingColumns: false,       // true while the columns request is in flight
      columnsLoadFailed: false,    // true if the request failed / returned an empty list
      includeQuestionnaireAnswers: true, // session only; the "Include questionnaire answers" toggle
    };
  },
  created(){
    this.selectedLanguage = this.$vuetify.lang.current;

    if (
      this.history_type === HISTORY_TYPES.CONVERSATION ||
      this.history_type === HISTORY_TYPES.SESSION ||
      this.history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE
    ) {
      this.getGPTProfiles();
    }
    if (this.history_type === HISTORY_TYPES.MAIRA_QUESTIONNAIRE) {
      this.getQuestionnaires();
    }
    if (this.supportsColumnSelection) {
      this.getAvailableColumns();
    }
  },
  computed: {
    ...mapState({
      account: (state) => state.account.account,
      selectedProject: (state) => state.project.selectedProject,
      dateRangeText: function () {
        return this.fileDateRange.join(" ~ ");
      },
    }),
    dialogWidth() {
      return window.innerWidth <= 768 ? '95%' : '57%';
    },
    // ---------------------------------------------------------------------------------
    // [CSV column selection] computed helpers
    // ---------------------------------------------------------------------------------
    // Which modal types get the column section. Questionnaire exports are out of scope.
    supportsColumnSelection() {
      return (
        this.history_type === HISTORY_TYPES.CONVERSATION ||
        this.history_type === HISTORY_TYPES.SESSION
      );
    },
    // Locale key for the section title ("Select Conversation Columns" / "Select Session Columns").
    columnSelectionTitleKey() {
      return this.history_type === HISTORY_TYPES.SESSION
        ? "select_session_columns_title"
        : "select_columns_title";
    },
    // "Active" = the section applies AND the list actually loaded. When the fetch failed this is
    // false, which makes every guard below fall back to the backend default columns.
    isColumnSelectionActive() {
      return this.supportsColumnSelection && this.availableColumns.length > 0;
    },
    // Drives the "Select all" button's disabled state (every key ticked, opt-in ones included).
    allColumnsSelected() {
      return this.isColumnSelectionActive && this.selectedColumns.length === this.availableColumns.length;
    },
    // Keys the backend flagged selected_by_default. This is what the modal ticks on open and
    // what a request WITHOUT `columns` produces server-side. For conversations that is 35 of the
    // 37 available columns (all except the opt-in intro/system); for sessions all 21.
    
    defaultColumnKeys() {
      return this.availableColumns
        .filter(column => column.selected_by_default !== false)
        .map(column => column.key);
    },
    // True when the ticked set is exactly the default set (order-insensitive).
    defaultColumnsSelected() {
      if (!this.isColumnSelectionActive) return false;
      const defaults = this.defaultColumnKeys;
      if (this.selectedColumns.length !== defaults.length) return false;
      const ticked = new Set(this.selectedColumns);
      return defaults.every(key => ticked.has(key));
    },
    // What goes into the request:
    //   null  -> backend default columns (sent when the ticked set == default set).
    //   [...] -> exactly these keys (any order; backend re-sorts to canonical order). This is
    //            also what "Select all" ends up sending, so the opt-in columns are included.
    columnsPayload() {
      if (!this.isColumnSelectionActive || this.defaultColumnsSelected) return null;
      return [...this.selectedColumns];
    },
    // Session only: the questionnaire toggle. Non-session types return undefined so the store
    // action never adds the field for conversation requests.
    includeQuestionnaireAnswersPayload() {
      if (this.history_type !== HISTORY_TYPES.SESSION) return undefined;
      return this.includeQuestionnaireAnswers;
    },
    // Generate is blocked while the list loads and when nothing is ticked
    isGenerateDisabled() {
      if (!this.supportsColumnSelection) return false;
      return this.loadingColumns || (this.isColumnSelectionActive && this.selectedColumns.length === 0);
    },
  },

  methods: {
    ...mapActions("project", [GET_GPT_PROFILES, MAIRA_QUESTIONNAIRE, GET_CONVERSATION_HISTORY_COLUMNS, GET_SESSION_HISTORY_COLUMNS]),
    ...mapActions("task", [GET_TASKS_BY_PREFIX]),
    ...mapActions("dataset", [GENERATE_PRESIGNED_URL]),
    ...mapActions("alert", [ADD_ALERT]),
    getFileExpirationFromTaskName,
    getDownloadUrl,
    getRequestTime,
    getDateRange,
    mapLocaleToLanguage,
    setDates() {
      this.$refs.fileDateRangeModal.save(this.fileDateRange);
    },
    getTasks({ page, itemsPerPage }){
      if(!this.fileDateRange[0] && !this.fileDateRange[1])
          return
      this.currentPage = page;
      this.itemsPerPage = itemsPerPage;
      
      const start = (page - 1) * itemsPerPage;
      const size = itemsPerPage;
      this.getFileGeneratedHistory(start, size)
    },

    getConversationHistory(){
        this.getFileGeneratedHistory(0, 10)
        this.loadingAvailableHistory = true
    },
    async getFileGeneratedHistory(start, size) {
      if(this.fileGeneratedHistoryData.length == 0)
        if (!this.$refs.fileGenerationForm.validate()) return;
      this.fileGeneratedHistoryLoading = true;
      this.fileGenerationLoading = true;
      let task_prefix =  `${this.prefix}_${this.$route.query.pid}_${this.fileDateRange[0]}_${this.fileDateRange[1]}`

      this.GET_TASKS_BY_PREFIX({
        project_id: this.selectedProject.id,
        task_prefix,
        task_status: "all_status",
        start: start,
        size: size,
        locale: this.selectedLanguage ? this.selectedLanguage : null
      })
        .then(
          (response) => {
            
            this.fileGeneratedHistoryData = response.data?.detail?.response;
            if(this.fileGeneratedHistoryData.length > 0){
              this.fileGeneratedHistory = true
              this.totalHits = response.data?.detail?.total_tasks;
            } else {
              this.downloadRequest()

            }
          },
          (error) => {
            console.error(error);
          }
        )
        .finally(() => {
          this.fileGeneratedHistoryLoading = false;
          this.fileGenerationLoading = false;
        });
    },
    async downloadRequest() {
      this.fileGenerationLoading = true;

      const params = {
        project_id: this.selectedProject.id,
        start_date: this.fileDateRange[0],
        end_date: this.fileDateRange[1],
        language: this.selectedLanguage,
        profile_id: this.selectedProfileId || null,
        profile_ids:
          this.selectedProfileIds?.length > 0 ? this.selectedProfileIds : null,
        session_id: this.filterBySessionId || null,
        session_code: this.filterByCode || null,
        search_text: this.searchText || null,
        questionnaire_ids:
          this.selectedQuestionnaireIds?.length > 0
            ? this.selectedQuestionnaireIds
            : null,
        user_id: this.filterByUserId || null,
        member_id: this.filterByMemberId || null,
        feedback_type: this.feedbackFilter || null,
        request_url: this.endpointFilter || null,
        message_seen: this.statusFilter ?? null,
        is_archived: this.archiveFilter ?? null,
        columns: this.columnsPayload,
        // [CSV column selection - session] questionnaire toggle; undefined for other types.
        include_questionnaire_answers: this.includeQuestionnaireAnswersPayload,
      };

      try {
        const response = await this.requestFileGeneration(params);
        this.response = response?.data?.detail;
        this.newDownloadRequest = true;
      } catch (_error) {
        this.newDownloadRequest = false;
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t(
            "$vuetify.logs_file_generation.file_generation_failed"
          ),
          color: "error",
        });
      } finally {
        this.fileGenerationLoading = false;
      }
    },

    closeResponse() {
      this.closeModal();
      this.response = false;
    },
    generatePresignedUrl(url){
      const match = url.match(/\/contents\/(.*?)\.csv/);
      if(match){
        const s3_key = `contents/${match[1]}.csv`
        this.GENERATE_PRESIGNED_URL({project_id: this.selectedProject.id,s3_key}).then(res => {
          const a = document.createElement("a");
          a.href = res.data.detail.download_url;
          a.download = "";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }).catch(e=> {
          console.error("error", e)
        })
      } else  {
        this.$store.dispatch(
                `alert/${ADD_ALERT}`,
                {
                  message: this.$vuetify.lang.t(
                    "$vuetify.logs_file_generation.download_url_missing",
                  ),
                  color: "error",
                },
                { root: true },
        );
      }

    },
    // ---------------------------------------------------------------------------------
    // [CSV column selection] methods
    // ---------------------------------------------------------------------------------
    async getAvailableColumns() {
      if (this.loadingColumns) return;
      this.loadingColumns = true;
      this.columnsLoadFailed = false;
      try {
        const fetchColumns =
          this.history_type === HISTORY_TYPES.SESSION
            ? this.GET_SESSION_HISTORY_COLUMNS
            : this.GET_CONVERSATION_HISTORY_COLUMNS;
        const apiRes = await fetchColumns({
          project_id: this.selectedProject.id,
          language: this.$vuetify.lang.current,
        });
        const columns = apiRes?.data?.detail?.response?.columns;
        this.availableColumns = Array.isArray(columns) ? columns : [];
        this.selectedColumns = this.defaultColumnKeys;
        this.columnsLoadFailed = this.availableColumns.length === 0;
        if (this.columnsLoadFailed) {
          console.warn("[file-generation] columns endpoint returned no usable columns; falling back to defaults");
        }
      } catch (e) {
        // Network/API error: same fallback. No alert toast; there is a inline warning
        this.availableColumns = [];
        this.selectedColumns = [];
        this.columnsLoadFailed = true;
      } finally {
        this.loadingColumns = false;
      }
    },
    // Ticks EVERY key, opt-in ones included -> the payload becomes the explicit full list.
    selectAllColumns() {
      this.selectedColumns = this.availableColumns.map(column => column.key);
    },
    // Unticks every key (opt-in ones included) -> Generate is disabled until something is ticked.
    clearAllColumns() {
      this.selectedColumns = [];
    },
    async getGPTProfiles() {
      try {
        if (this.loadingProfiles) return;
        this.loadingProfiles = true;
        
        const apiRes = await this.GET_GPT_PROFILES({
          project_id: this.$route.query.pid,
          start: this.currentPage * this.pageSize,
          size: this.pageSize,
        });
        const responseData = apiRes?.data?.detail?.response;
        const newProfiles = responseData?.profiles || [];
        const totalHits = responseData?.total_hits || 0;
        this.gptProfiles = this.currentPage === 0 ? newProfiles : [...this.gptProfiles, ...newProfiles];

        this.hasMoreProfiles = this.gptProfiles.length < totalHits;
        this.currentPage++;
      } catch (e) {
        this.$store.dispatch(`alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t('$vuetify.gpt_profile.profiles_fetch_error'),
          color: 'error'
        }, { root: true });
      } finally {
        this.loadingProfiles = false;
      }
    },
    async getQuestionnaires() {
      if (this.loadingQuestionnaires) return;
      this.loadingQuestionnaires = true;
      try {
        const apiRes = await this.MAIRA_QUESTIONNAIRE({
          project_id: this.selectedProject.id,
          start: this.questionnairePage * this.questionnairePageSize,
          size: this.questionnairePageSize,
        });
        const responseData = apiRes?.data?.detail?.response;
        const newQuestionnaires = responseData?.questionnaires || [];
        const totalHits = responseData?.total_hits || 0;
        this.questionnaires =
          this.questionnairePage === 0
            ? newQuestionnaires
            : [...this.questionnaires, ...newQuestionnaires];
        this.hasMoreQuestionnaires = this.questionnaires.length < totalHits;
        this.questionnairePage++;
      } catch (e) {
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.questionnaire_fetch_error"
          ),
          color: "error",
        });
      } finally {
        this.loadingQuestionnaires = false;
      }
    },
    removeQuestionnaire(item) {
      const index = this.selectedQuestionnaireIds.indexOf(item.id);
      if (index !== -1) {
        this.selectedQuestionnaireIds.splice(index, 1);
      }
    },
    removeProfile(item) {
      const index = this.selectedProfileIds.indexOf(item.id);
      if (index !== -1) {
        this.selectedProfileIds.splice(index, 1);
      }
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(newVal) {
        this.openDialogue = newVal;
      }
    },
    openDialogue(newVal) {
      this.$emit('update:open', newVal);
    }
  },
};
</script>

<style scoped>
.field-spacing {
  margin-bottom: 16px !important;
}

.field-spacing:last-child {
  margin-bottom: 0 !important;
}

@media (min-width: 960px) {
  .field-spacing {
    margin-bottom: 0 !important;
  }
}

.v-select.v-input--dense .v-chip {
  margin: 6px 4px 0px 4px;
}

.file-history-table {
  overflow-x: auto;
}

@media (max-width: 600px) {
  .file-history-table .v-data-table__wrapper {
    overflow-x: auto;
  }
}

.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--box.v-select--chips--small.v-input--dense >>> .v-select__selections:has(.v-chip),
.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed.v-select--chips--small.v-input--dense >>> .v-select__selections:has(.v-chip) {
  margin-top: 6px;
  margin-bottom: 6px;
}
</style>